import { TaskType } from '@/shared/domain/taskTypes';
import { isFallbackSentinel } from './fallbackLabel';
import {
    LABEL_PRIORITY_KIND,
    labelPriorityAppliesTo,
    labelPriorityOptionText,
    reconcileLabelPriority,
} from './labelPriority';

/**
 * The task label catalog ("Labels in this task") can change outside the
 * Business Goals step: the Data Collection Fix menu ("Update labels in
 * Business Goals step") replaces it with what the uploaded files hold. The
 * two Business Goals choices that point at a label - the Fallback label
 * select and "What matters most for these labels?" - then have to follow,
 * the same way they follow a label edit made on the step itself.
 *
 * `LABEL_CHOICE_FIELD` values double as the sections' `data-walkthrough`
 * anchors, so the wizard can send the user back to the right select.
 */
export const LABEL_CHOICE_FIELD = Object.freeze({
    fallback: 'fallback-label',
    priority: 'label-priority',
});

/**
 * Apply a new label catalog to the wizard form the way Business Goals'
 * `update({ labels })` does: a fallback label that is gone is cleared (the
 * Best guess / No predictions sentinels stay), the "What matters most" choice
 * is dropped when its label is gone or the section stops applying (and the
 * fallback it derived with it), or re-derives the fallback when it still
 * applies; a label spelled "other" becomes the default fallback when none is
 * left. Returns a new form.
 */
export function reconcileFormForLabels(form, labels) {
    const next = Array.isArray(labels) ? labels : [];
    const patch = { labels: next };
    if (form.taskType !== TaskType.classification) return { ...form, ...patch };

    if (form.fallbackLabel && !isFallbackSentinel(form.fallbackLabel) && !next.includes(form.fallbackLabel)) {
        patch.fallbackLabel = null;
        patch.fallbackLabelUserSet = false;
    }
    Object.assign(
        patch,
        reconcileLabelPriority({
            taskType: form.taskType,
            classificationType: form.classificationType,
            labels: next,
            labelPriority: form.labelPriority,
            fallbackLabelUserSet: patch.fallbackLabelUserSet ?? form.fallbackLabelUserSet ?? false,
        })
    );
    const fallbackAfter = 'fallbackLabel' in patch ? patch.fallbackLabel : form.fallbackLabel;
    if (!fallbackAfter) {
        const otherLabel = next.find((label) => label.toLowerCase() === 'other');
        if (otherLabel) patch.fallbackLabel = otherLabel;
    }
    return { ...form, ...patch };
}

/**
 * The required Business Goals label choice the form currently lacks, if any:
 * `LABEL_CHOICE_FIELD.priority` when the step shows "What matters most for
 * these labels?" (single-label, exactly two labels) without a choice,
 * `LABEL_CHOICE_FIELD.fallback` when it shows the Fallback label select
 * without a value, else `null`. Mirrors the step's own section rules.
 */
export function missingLabelChoiceField(form) {
    if (form?.taskType !== TaskType.classification || !form.classificationType) return null;
    const labels = Array.isArray(form.labels) ? form.labels : [];
    if (!labels.length) return null;
    const prioritySection = labelPriorityAppliesTo({ classificationType: form.classificationType, labels });
    if (prioritySection && !form.labelPriority) return LABEL_CHOICE_FIELD.priority;
    const fallbackSection = !prioritySection || form.labelPriority?.kind === LABEL_PRIORITY_KIND.equal;
    if (fallbackSection && !form.fallbackLabel) return LABEL_CHOICE_FIELD.fallback;
    return null;
}

const quoteList = (labels = []) => (labels.length ? labels.map((label) => `"${label}"`).join(', ') : 'no labels yet');

/**
 * Copy of the "your choice points at a label the task no longer has" modal
 * shown by Data Collection after the Fix replaced the catalog, and again on
 * Next while the choice is still missing.
 *
 * @param {object} params
 * @param {string} params.field Which select is empty (`LABEL_CHOICE_FIELD`).
 * @param {string[]} params.labels The task labels now.
 * @param {string|null} [params.removedFallback] Fallback label the Fix removed.
 * @param {object|null} [params.previousPriority] One-sided "What matters most"
 *   choice the Fix invalidated (`{ kind, label }`).
 * @returns {{ title: string, body: string, primaryLabel: string }}
 */
export function lostLabelChoiceCopy({ field, labels = [], removedFallback = null, previousPriority = null }) {
    const labelsText = quoteList(labels);
    if (field === LABEL_CHOICE_FIELD.priority) {
        const title = 'Label priority needs a new choice';
        const primaryLabel = 'Adjust Label Priorities';
        if (previousPriority?.label) {
            return {
                title,
                primaryLabel,
                body:
                    `In Business Goals you chose ${labelPriorityOptionText(previousPriority)}, but ` +
                    `"${previousPriority.label}" is no longer one of this task's labels (${labelsText}). ` +
                    'Go back to Business Goals and choose what matters most for the new labels - ' +
                    'that choice also sets the fallback label.',
            };
        }
        if (removedFallback) {
            return {
                title,
                primaryLabel,
                body:
                    `The fallback label "${removedFallback}" is no longer one of this task's labels ` +
                    `(${labelsText}). With two labels, Business Goals asks what matters most for them, ` +
                    'which also sets the fallback label. Go back and choose it.',
            };
        }
        return {
            title,
            primaryLabel,
            body:
                `This task has no "What matters most for these labels?" choice for its labels ` +
                `(${labelsText}). Go back to Business Goals and choose it.`,
        };
    }
    return {
        title: 'Fallback label no longer exists',
        primaryLabel: 'Adjust Fallback',
        body: removedFallback
            ? `"${removedFallback}" was the fallback label, but it is no longer one of this task's labels ` +
              `(${labelsText}). Go back to Business Goals and choose a new fallback label.`
            : `This task has no fallback label for its labels (${labelsText}). Go back to Business Goals ` +
              'and choose one.',
    };
}
